import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SettlementStatusFilter } from '~store/SettlementsStore';

export const SETTLEMENT_STATUS_PARAM = 'settlement_status';

const VALID_STATUS_FILTERS = new Set(Object.values(SettlementStatusFilter));

function isValidSettlementStatusFilter(value: string): value is SettlementStatusFilter {
  return VALID_STATUS_FILTERS.has(value as SettlementStatusFilter);
}

export function parseSettlementStatusFilters(
  param: string | null,
): SettlementStatusFilter[] {
  if (!param) {
    return [];
  }
  return param.split('|').filter(isValidSettlementStatusFilter);
}

interface StatusFilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function StatusFilterChip({ label, isActive, onClick }: StatusFilterChipProps) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        alignItems: 'center',
        bgcolor: isActive
          ? theme.brandV2.colors.treadGray7
          : theme.semantic.surfaceRaised,
        border: `solid 1px ${isActive ? theme.brandV2.colors.treadGray5 : theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        px: 1.2,
        py: 0.5,
        borderRadius: '48px',
        cursor: 'pointer',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function SettlementStatusFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedFilters = useMemo(() => {
    return parseSettlementStatusFilters(searchParams.get(SETTLEMENT_STATUS_PARAM));
  }, [searchParams]);

  const toggleFilter = useCallback(
    (filter: SettlementStatusFilter) => {
      setSearchParams((params) => {
        const currentFilters = parseSettlementStatusFilters(
          params.get(SETTLEMENT_STATUS_PARAM),
        );
        const isActive = currentFilters.includes(filter);

        let newFilters: SettlementStatusFilter[];
        if (isActive) {
          newFilters = currentFilters.filter((f) => f !== filter);
        } else {
          newFilters = [...currentFilters, filter];
        }

        if (newFilters.length === 0) {
          params.delete(SETTLEMENT_STATUS_PARAM);
        } else {
          params.set(SETTLEMENT_STATUS_PARAM, newFilters.join('|'));
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const filters = [
    {
      value: SettlementStatusFilter.PENDING,
      label: t('approvals.driver_pay.settlement_status_filters.pending'),
    },
    {
      value: SettlementStatusFilter.APPROVED,
      label: t('approvals.driver_pay.settlement_status_filters.approved'),
    },
    {
      value: SettlementStatusFilter.DONE,
      label: t('approvals.driver_pay.settlement_status_filters.done'),
    },
    {
      value: SettlementStatusFilter.VOID,
      label: t('approvals.driver_pay.settlement_status_filters.void'),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
      }}
    >
      {filters.map(({ value, label }) => (
        <StatusFilterChip
          key={value}
          label={label}
          isActive={selectedFilters.includes(value)}
          onClick={() => toggleFilter(value)}
        />
      ))}
    </Box>
  );
}
